import { useEffect, useMemo, useState } from "react";
import { absUrl, STEM_META } from "@/lib/api";
import { useSyncedPlayer } from "@/components/qc/useSyncedPlayer";
import { Waveform } from "@/components/qc/Waveform";
import { TransportBar } from "@/components/qc/TransportBar";

// Side-by-side listening: the same stem from every engine, one master clock, solo per engine.
// Hotkeys: 1-9 solo the Nth engine lane instantly (playback keeps running), 0 = hear original, Esc = clear solo.
export const SideBySide = ({ jobs, original }) => {
  const completed = jobs.filter((j) => j.status === "completed" && j.output_files);
  const stemsAvailable = ["dialogue", "music", "effects", "me"].filter((s) => completed.some((j) => j.output_files?.[s]));
  const [stem, setStem] = useState(stemsAvailable.includes("effects") ? "effects" : stemsAvailable[0]);
  const lanes = completed.filter((j) => j.output_files?.[stem]);
  const tracks = useMemo(() => {
    const t = original ? [{ key: "original", url: absUrl(original.stream_url) }] : [];
    lanes.forEach((j) => t.push({ key: j.id, url: absUrl(j.output_files[stem].stream_url) }));
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed, original, stem]);
  const player = useSyncedPlayer(tracks);
  const laneIds = lanes.map((j) => j.id).join("|");
  useEffect(() => {
    const ids = laneIds ? laneIds.split("|") : [];
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (e.key === "0") player.soloOnly("original");
      else if (e.key === "Escape") player.soloOnly(null);
      else if (/^[1-9]$/.test(e.key) && ids[Number(e.key) - 1]) player.soloOnly(ids[Number(e.key) - 1]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [laneIds, player]);
  if (!completed.length) return <div data-testid="benchmark-listen-empty" className="panel p-8 text-center text-slate-500 text-sm">Side-by-side listening becomes available once at least one engine completes.</div>;
  const rows = [{ key: "original", hot: "0", label: "Original mix", file: original, color: STEM_META.original.color },
    ...lanes.map((j, i) => ({ key: j.id, hot: i < 9 ? String(i + 1) : "", label: j.engine_name, file: j.output_files[stem], color: STEM_META[stem].color }))];
  return (
    <div data-testid="benchmark-side-by-side" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="micro">Listen to stem</span>
        {stemsAvailable.map((s) => (
          <button key={s} data-testid={`benchmark-stem-${s}-button`} onClick={() => setStem(s)}
            className={`px-3 h-8 rounded-sm border text-xs font-mono uppercase tracking-wider transition-colors ${stem === s ? "text-white" : "border-[#3A4A68] text-slate-400 hover:bg-[#1E2738]"}`}
            style={stem === s ? { borderColor: STEM_META[s].color, background: `${STEM_META[s].color}22` } : undefined}>{STEM_META[s].short}</button>
        ))}
        <span data-testid="benchmark-hotkey-hint" className="text-xs text-slate-500 ml-2 font-mono">Hotkeys: 1–{Math.min(lanes.length, 9)} flip engine while playing · 0 original · Esc all</span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const on = player.audible(r.key), soloed = player.solo.includes(r.key), mutedFlag = player.muted.includes(r.key);
          return (
            <div key={r.key} data-testid={`benchmark-lane-${r.key}`} className={`grid grid-cols-[230px_1fr] gap-3 rounded-md border bg-[#121721] transition-colors ${soloed ? "border-amber-400/70" : "border-[#1c2433]"}`}>
              <div className="p-3 border-r border-[#1c2433] flex flex-col justify-between">
                <div className="flex items-start gap-2">
                  {r.hot && <kbd data-testid={`benchmark-hotkey-${r.key}`} className={`mt-0.5 h-5 min-w-[20px] px-1 grid place-items-center rounded-sm text-[11px] font-mono font-bold border ${soloed ? "bg-amber-400 text-black border-amber-400" : "border-[#3A4A68] text-slate-400"}`}>{r.hot}</kbd>}
                  <div>
                    <div className="text-sm font-medium text-slate-100 leading-tight">{r.label}</div>
                    <div className="text-[10px] font-mono text-slate-500 mt-0.5">{r.key === "original" ? "reference" : `${stem.toUpperCase()} stem`}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <button data-testid={`benchmark-solo-${r.key}-button`} disabled={!r.file} onClick={() => player.toggleSolo(r.key)} className={`h-7 w-8 rounded-sm text-xs font-bold border ${soloed ? "bg-amber-400 text-black border-amber-400" : "border-[#3A4A68] text-slate-300 hover:bg-[#1E2738]"}`}>S</button>
                  <button data-testid={`benchmark-mute-${r.key}-button`} disabled={!r.file} onClick={() => player.toggleMute(r.key)} className={`h-7 w-8 rounded-sm text-xs font-bold border ${mutedFlag ? "bg-rose-500 text-white border-rose-500" : "border-[#3A4A68] text-slate-300 hover:bg-[#1E2738]"}`}>M</button>
                  <span className={`ml-1 h-2 w-2 rounded-full ${on ? "bg-emerald-400" : "bg-slate-700"}`} />
                </div>
              </div>
              <div className="p-2 pr-3 flex items-center">
                <Waveform peaks={r.file?.peaks || []} color={r.color} progress={player.duration ? player.time / player.duration : 0} dim={!on} height={64}
                  onSeek={(p) => player.seek(p * player.duration)} testId={`benchmark-waveform-${r.key}`} />
              </div>
            </div>
          );
        })}
      </div>
      <TransportBar player={player} />
    </div>
  );
};
